import { List } from "immutable";
import { useAtom } from "jotai";
import EnhancedContentRecord from "models/view-models/enhanced-content-record";
import { useState } from "react";
import { useLocation } from "react-router-dom";
import EnhancedContentByAnnexService from "utilities/services/enhanced-content/enhanced-content-by-annex-service";
import EnhancedContentByChapterService from "utilities/services/enhanced-content/enhanced-content-by-chapter-service";
import { ToastManager } from "utilities/toast/toast-manager";
import CurrentPublicationEnhancedContent from "./enhanced-content-atom";
import EnhancedContentResourceService from "utilities/services/enhanced-content-resources/enhanced-content-resource-service";
import FileService from "utilities/services/file-service";
import { CollectionUtils } from "utilities/collection-utils";
import EnhancedContentService from "utilities/services/sections/enhanced-content/enhanced-content-service";
import EnhancedContentDetailsService from "utilities/services/enhanced-content/enhanced-content-details-service";
import CurrentPublicationEnhancedContentManifests from "./enhanced-content-manifests-atom";
import EnhancedContentChapterManifestsService from "utilities/services/enhanced-content/enhanced-content-chapter-manifests-service";
import EnhancedContentManifestRecord from "models/view-models/enhanced-content-manifest-record";

interface UseEnhancedContentOptions {
    chapterNfpaLabel?: string;
    annexNfpaLabel?: string;
    code?: string;
    edition?: string;
    isAdmin?: boolean;
    externalId?: string;
    chapterId?: number;
    annexId?: number;
    disabled?: boolean;
}

const BATCH_REQUEST_SIZE = 25;

const useEnhancedContent = (options: UseEnhancedContentOptions) => {
    const {
        chapterNfpaLabel,
        annexNfpaLabel,
        code,
        edition,
        externalId,
        chapterId,
        annexId,
        isAdmin = false,
        disabled = false,
    } = options;

    const [enhancedContent, setEnhancedContent] = useAtom(
        CurrentPublicationEnhancedContent
    );

    const [enhancedContentManifests, setEnhancedContentManifests] = useAtom(
        CurrentPublicationEnhancedContentManifests
    );

    const [loadingEnhancedContent, setLoadingEnhancedContent] = useState(false);
    const { list: listEnhancedContent } = EnhancedContentService.useBatchList();
    const { list: listResources } =
        EnhancedContentResourceService.useBatchList();
    const { list: listFiles } = FileService.useBatchedList();

    const { pathname } = useLocation();
    const isChapter = pathname.toString().includes("chapters");
    const isAnnex = pathname.toString().includes("annexes");

    const getEnhancedContentDetails = async (id) => {
        const alreadyHaveContent = enhancedContent.some(
            (content) => content.id === id
        );
        if (alreadyHaveContent || isAnnex) {
            return;
        }
        try {
            setLoadingEnhancedContent(true);
            const response = await EnhancedContentDetailsService.get({
                id: id,
            });
            setEnhancedContent((prev) => {
                const next = response.resultObject
                    ? [...prev, response.resultObject]
                    : prev;

                return next;
            });
            setLoadingEnhancedContent(false);
        } catch (error) {
            ToastManager.error(`Error getting NFPA Insights details: ${error}`);
        }
    };
    const getEnhancedContent = async () => {
        if (disabled) return;
        if (isAnnex) {
            await getAnnexEnhancedContent();
        } else {
            await getEnhancedContentManifests();
        }
    };
    const getEnhancedContentManifests = async () => {
        try {
            //only get manifests for chapters
            if (!isChapter) {
                return;
            }
            const response = await EnhancedContentChapterManifestsService.list({
                code: code ?? "",
                edition: edition ?? "",
                chapterNfpaLabel: chapterNfpaLabel ?? "",
            });
            setEnhancedContentManifests(response.resultObjects);
        } catch (error) {
            ToastManager.error(
                `Error getting NFPA Insights Manifests: ${error}`
            );
        }
    };
    const getAnnexEnhancedContentForAdmin = async () => {
        try {
            if (
                code === undefined ||
                edition === undefined ||
                (annexNfpaLabel === undefined && !isAdmin) ||
                (annexId === undefined && isAdmin)
            ) {
                return [];
            }
            setLoadingEnhancedContent(true);
            const response = await EnhancedContentByAnnexService.adminList({
                annexId: annexId?.toString() ?? "",
            });

            return response.resultObjects;
        } catch (error) {
            ToastManager.error(`Error getting NFPA Insights: ${error}`);
            return [];
        } finally {
            setLoadingEnhancedContent(false);
        }
    };

    const getAnnexEnhancedContent = async () => {
        try {
            if (
                code === undefined ||
                edition === undefined ||
                (annexNfpaLabel === undefined && !isAdmin) ||
                (annexId === undefined && isAdmin)
            ) {
                return [];
            }
            setLoadingEnhancedContent(true);
            const response = await EnhancedContentByAnnexService.list({
                code,
                edition,
                annexNfpaLabel: annexNfpaLabel ?? "",
            });

            setEnhancedContent(response.resultObjects);
        } catch (error) {
            ToastManager.error(`Error getting NFPA Insights: ${error}`);
            return [];
        } finally {
            setLoadingEnhancedContent(false);
        }
    };

    // Given a chaper, gets NFPA Insights for that chapter and returns it
    const getChapterEnhancedContentForAdmin = async () => {
        try {
            if (
                code === undefined ||
                edition === undefined ||
                (chapterNfpaLabel === undefined && !isAdmin) ||
                (chapterId === undefined && isAdmin)
            ) {
                return [];
            }
            setLoadingEnhancedContent(true);
            const response = await EnhancedContentByChapterService.adminList({
                chapterId: chapterId?.toString() ?? "",
            });

            return response.resultObjects;
        } catch (error) {
            ToastManager.error(`Error getting NFPA Insights: ${error}`);
            return [];
        } finally {
            setLoadingEnhancedContent(false);
        }
    };

    const getEnhancedContentForReferencePanelSection = async (
        code: string,
        edition: string,
        externalIds: string[]
    ) => {
        try {
            // Grab all NFPA Insights matching the section external id(s)
            const enhancedContentResponse = await listEnhancedContent(
                { code, edition, externalIds },
                "externalIds",
                BATCH_REQUEST_SIZE
            );

            // Grab resources matching all NFPA Insights in panel
            const enhancedContentIds =
                enhancedContentResponse.resultObjects.map((element) => {
                    return element.id!;
                });
            const resourcesResponse = await listResources(
                { enhancedContentIds },
                "enhancedContentIds",
                BATCH_REQUEST_SIZE
            );

            // Query for files related to NFPA Insights resources
            const fileIds = CollectionUtils.uniqueValuesByProperty(
                resourcesResponse.resultObjects,
                "fileId"
            );
            const { resultObjects: files } = await listFiles(
                { ids: fileIds },
                "ids",
                BATCH_REQUEST_SIZE
            );

            // Merge in files to resources
            const resourcesWithFiles = resourcesResponse.resultObjects.map(
                (resource) => {
                    return resource.withFile(
                        files.filter((file) => file.id === resource.fileId)
                    );
                }
            );

            // Merge in resources to NFPA Insights
            return enhancedContentResponse.resultObjects.map((content) => {
                return content.with({
                    resources: resourcesWithFiles.filter(
                        (resource) => resource.enhancedContentId === content.id
                    ),
                });
            });
        } catch (error) {
            ToastManager.error(
                `Error getting NFPA Insights in reference panel: ${error}`
            );
        }
    };
    // Checks to see if NFPA Insights is needed on chapter change
    const updateAdminEnhancedContent = async () => {
        if (disabled) return;

        // If NFPA Insights is not available for that chapter,
        // and if we're not already calling for NFPA Insights, make the call
        if (
            code !== undefined &&
            edition !== undefined &&
            (chapterNfpaLabel !== undefined ||
                annexNfpaLabel !== undefined ||
                isAdmin) &&
            !loadingEnhancedContent
        ) {
            const results = isChapter
                ? await getChapterEnhancedContentForAdmin()
                : await getAnnexEnhancedContentForAdmin();
            // Add the NFPA Insights to that chapter id
            setEnhancedContent(results);
        }
    };

    const updateEnhancedContentOnEdit = (
        updatedEnhancedContent: EnhancedContentRecord
    ) => {
        const newEnhancedContentAtom = enhancedContent.map((element) => {
            return element.id === updatedEnhancedContent.id
                ? updatedEnhancedContent
                : element;
        });

        setEnhancedContent(newEnhancedContentAtom);
    };

    const updateEnhancedContentOnDelete = (idToDelete: number | undefined) => {
        const newEnhancedContentAtom = enhancedContent.filter(
            (element) => element.id !== idToDelete
        );
        const newEnhancedContentManifests = enhancedContentManifests.filter(
            (element) => element.id !== idToDelete
        );
        setEnhancedContent(newEnhancedContentAtom);
        setEnhancedContentManifests(newEnhancedContentManifests);
    };

    const updateEnhancedContentOnAdd = (
        newEnhancedContent: EnhancedContentRecord
    ) => {
        setEnhancedContent((prev) => [...prev, newEnhancedContent]);
        setEnhancedContentManifests((prevManifests) => [
            ...prevManifests,
            new EnhancedContentManifestRecord({
                id: newEnhancedContent.id,
                externalId: newEnhancedContent.externalId,
            }),
        ]);
    };
    const enhancedContentForCurrentCaapsItem =
        enhancedContent?.find((element) => element.externalId === externalId) ??
        undefined;
    // NFPA Insights resources are typed as Lists, not arrays. Not sure why, but adding this here is easier than changing it everywhere
    const enhancedContentResourcesForCurrentCaapsItem = List(
        enhancedContentForCurrentCaapsItem?.resources ?? []
    );

    const currentEnhancedContentManifest =
        enhancedContentManifests.find(
            (element) => element.externalId === externalId
        ) ?? null;

    return {
        enhancedContent,
        getEnhancedContent,
        loadingEnhancedContent,
        currentEnhancedContentManifest,
        enhancedContentForCurrentCaapsItem,
        enhancedContentResourcesForCurrentCaapsItem,
        updateAdminEnhancedContent,
        updateEnhancedContentOnAdd,
        updateEnhancedContentOnEdit,
        updateEnhancedContentOnDelete,
        getEnhancedContentForReferencePanelSection,
        getEnhancedContentManifests,
        getEnhancedContentDetails,
    };
};

export default useEnhancedContent;
